import { useEffect, useRef, useState } from 'react';

import {
  faCircleInfo,
  faDownLeftAndUpRightToCenter,
  faUpRightAndDownLeftFromCenter,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { useHistory } from 'react-router-dom';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import useKeypress from 'react-use-keypress';

import type { FmxIndexesByType } from '@/@types/latestImagesV2';
import {
  selectTeethAtom,
  useLocalizedFormatDate,
} from '@/modules/claims/freeClaims/state';
import type { TVisitSummary } from '@/react-query/useVisitSummaries';

import { usePreferences } from '../../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { latestImagesVisitDateAtom } from '../../atoms';
import { disableKeyPressAtom } from '../../casePresentation/atoms';
import { ToothLabel } from '../../casePresentation/toolbar/CasePresentationImageTracker';
import CurrentImageTracker from '../../currentImageTracker/CurrentImageTracker';
import { useImageFindingCounts } from '../../findingSidebar/confidenceScoreFilter/helpers';
import { ALL_KEYS, KEY_MAP } from '../../utils';
import { useTrackImageIndex } from '../findingViewer/imageBrowser/helpers';
import { isExpandedAtom, showSegmentationAtom } from '../imageToolbar/atoms';

import { FmxSortButton } from './FmxSort/FmxSortButton';
import { ImageFilter } from './ImageFilter';
import { ImageGrid } from './ImageGrid';

type ImageViewerFooterProps = {
  activeImage: any;
  currentImageFilter: string;
  numberOfBitewings: number;
  numberOfSortedFMXImages: number;
  lastAnalysisDate?: Date;
  isHQEnabled?: boolean;
  onSetCurrentImageFilter: (filter: string) => void;
  onSetIsHQEnabled: (enabled: boolean) => void;
  visitSummary?: TVisitSummary;
  shouldUseSeriesView?: boolean;
  fmxIndexesByType?: FmxIndexesByType[];
};

export const ImageViewerFooter = ({
  activeImage,
  currentImageFilter,
  numberOfBitewings,
  numberOfSortedFMXImages,
  lastAnalysisDate,
  isHQEnabled,
  onSetCurrentImageFilter,
  onSetIsHQEnabled,
  visitSummary,
  shouldUseSeriesView = false,
  fmxIndexesByType,
}: ImageViewerFooterProps) => {
  const { t } = useLingui();
  const formatDate = useLocalizedFormatDate();
  const [isExpanded, setIsExpanded] = useAtom(isExpandedAtom);
  const visitDate = useAtomValue(latestImagesVisitDateAtom);
  const selectedTeeth = useAtomValue(selectTeethAtom);
  const showSegmentation = useAtomValue(showSegmentationAtom);
  const { trackEvent } = useTrackEvent();
  const prevActiveImageRef = useRef<string | undefined>();
  const { data: prefs } = usePreferences();
  const nextImageIds = useTrackImageIndex();
  const history = useHistory();
  const shouldDisableKeyPress = useAtomValue(disableKeyPressAtom);

  const {
    allFindingsTotal,
    allFindingsHiddenTotal,
    allFindingsAddedTotal,
    findingsDescription,
  } = useImageFindingCounts(activeImage);
  // Pagination state for dots in footer header
  const [paginationData, setPaginationData] = useState<{
    currentPage: number;
    totalPages: number;
    hasImages: boolean;
  }>({ currentPage: 1, totalPages: 1, hasImages: false });

  const handlePaginationChange = (data: {
    currentPage: number;
    totalPages: number;
    hasImages: boolean;
  }) => {
    setPaginationData(data);
  };

  const handlePageChange = (page: number) => {
    setPaginationData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  useKeypress(ALL_KEYS, (event: { key: any }) => {
    if (shouldDisableKeyPress) return;
    switch (true) {
      case KEY_MAP.keyPressedNextImage.includes(event.key):
        moveIndexBy(nextImageIds?.next);
        break;
      case KEY_MAP.keyPressedPrevImage.includes(event.key):
        moveIndexBy(nextImageIds?.prev);
        break;
    }
  });

  const moveIndexBy = (nextImageId?: string) => {
    if (!nextImageId) {
      return;
    }

    let newRoute = `${history.location.pathname}/${nextImageId}`;
    if (activeImage) {
      newRoute = history.location.pathname.replace(
        activeImage.imageId,
        nextImageId || '',
      );
    }
    history.push({ pathname: newRoute, search: history.location.search });
  };

  // Need to default the HQ value once we get prefs back
  useEffect(() => {
    // Wait for preferences + active image to be defined before setting HQ toggle
    // Don't reset the HQ toggle once it's been set
    if (
      !prefs ||
      (!activeImage?.imageId && !prefs.enableHqOnThumbnails) ||
      isHQEnabled !== undefined
    ) {
      return;
    }

    onSetIsHQEnabled(
      activeImage?.userModifications?.hqEnabled ?? !prefs?.hqOffByDefault,
    );
    // Don't want to trigger effect anytime activeImage changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeImage?.userModifications?.hqEnabled,
    activeImage?.imageId,
    prefs,
    onSetIsHQEnabled,
  ]);

  useEffect(() => {
    const currentActiveImageId = activeImage?.imageId;
    const prevActiveImageId = prevActiveImageRef.current;

    // Only expand when transitioning from no active image to having an active image
    if (currentActiveImageId && !prevActiveImageId) {
      setIsExpanded(true);
    }

    // Update the ref for the next comparison
    prevActiveImageRef.current = currentActiveImageId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeImage?.imageId]);

  const handleExpandToggle = () => {
    const isNewExpandedState = !isExpanded;
    setIsExpanded(isNewExpandedState);
    trackEvent(
      isNewExpandedState
        ? mixpanelEvents.imageFooterExpand
        : mixpanelEvents.imageFooterCollapse,
      {
        unifiedAppointmentId: visitSummary?.unifiedAppointmentId,
        estimatedVisitId: visitSummary?.estimatedVisitId,
      },
    );
  };

  return (
    <>
      {!activeImage && !!selectedTeeth.length && <ToothLabel />}
      <div className="vai-flex vai-justify-center vai-items-center vai-bg-transparent vai-h-[40px]">
        {!!activeImage && (
          <CurrentImageTracker
            currentImageFilter={currentImageFilter}
            currentImageIndex={nextImageIds?.currentIndex}
            totalImages={nextImageIds?.totalImages}
            onPreviousClick={() => moveIndexBy(nextImageIds?.prev)}
            onNextClick={() => moveIndexBy(nextImageIds?.next)}
            activeImage={activeImage}
          />
        )}
      </div>
      <div className="vai-w-full vai-bg-transparent vai-px-2 vai-pt-2 vai-pb-0 vai-rounded-md vai-sticky vai-bottom-0 vai-z-10 vai-min-h-[60px] vai-transition-all vai-duration-200 vai-flex vai-flex-col vai-font-inter">
        {!activeImage && (
          <div className="vai-flex vai-items-center vai-justify-between">
            <div className="vai-flex vai-gap-2 vai-mb-2">
              {!!visitSummary && !!fmxIndexesByType?.length && (
                <FmxSortButton
                  estimatedVisitId={visitSummary.estimatedVisitId}
                  fmxIndexesByType={fmxIndexesByType}
                />
              )}
              <ImageFilter
                numberOfBitewings={numberOfBitewings}
                numberOfSortedFMXImages={numberOfSortedFMXImages}
                onSetCurrentImageFilter={onSetCurrentImageFilter}
                currentImageFilter={currentImageFilter}
                visitSummary={visitSummary}
                shouldUseSeriesView={shouldUseSeriesView}
              />
            </div>
            {showSegmentation && (
              <p className="vai-font-inter vai-text-xs vai-text-zinc-500">
                <Trans>Visualization is intended for patient education.</Trans>
              </p>
            )}
          </div>
        )}
        {/* Footer Bar */}
        <div
          data-testid="image-footer-bar"
          className={classNames(
            'vai-flex vai-rounded-t-md vai-px-4 vai-items-center vai-justify-between vai-relative vai-bg-background-gray vai-flex-shrink-0 vai-font-inter',
            {
              'vai-pt-1': isExpanded,
              'vai-py-1': !isExpanded,
            },
          )}
        >
          {!!visitDate && (
            <div className="vai-flex vai-items-center vai-gap-2 vai-text-zinc-400 vai-font-sans vai-font-medium vai-text-sm">
              <span>
                {t`Visit Date`}: {formatDate(visitDate.toISOString())}
              </span>
              {lastAnalysisDate && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <FontAwesomeIcon
                        icon={faCircleInfo}
                        className="vai-text-foreground-muted vai-h-3 vai-w-3"
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent variant="dark">
                    <p className="vai-text-sm vai-font-inter vai-font-medium">
                      <Trans>Date of last AI analysis</Trans>:{' '}
                      {formatDate(lastAnalysisDate.toISOString())}
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {/* Pagination Dots - Center of footer header */}
          {paginationData.hasImages &&
            paginationData.totalPages > 1 &&
            isExpanded && (
              <div className="vai-absolute vai-left-1/2 vai-top-1/2 vai-transform -vai-translate-x-1/2 -vai-translate-y-1/2 vai-flex vai-items-center vai-gap-1">
                {Array.from(
                  {
                    length: Math.max(
                      1,
                      Number.isFinite(paginationData.totalPages)
                        ? paginationData.totalPages
                        : 1,
                    ),
                  },
                  (_, i) => (
                    <div
                      key={i}
                      className={classNames(
                        'vai-w-2 vai-h-2 vai-rounded-full vai-transition-colors vai-duration-200',
                        paginationData.currentPage === i + 1
                          ? 'vai-bg-sapphire-600'
                          : 'vai-bg-zinc-600',
                      )}
                    />
                  ),
                )}
              </div>
            )}

          <div className="vai-flex vai-justify-end vai-items-center vai-gap-2">
            {activeImage &&
              (activeImage.detectedImageType === 'BITEWING' ||
                activeImage.detectedImageType === 'PERIAPICAL') && (
                <div className="vai-flex vai-items-center vai-gap-2 vai-text-zinc-400 vai-font-sans vai-font-medium vai-text-sm">
                  <span>
                    {allFindingsTotal} <Trans>Findings</Trans>
                  </span>
                  <span>
                    {allFindingsHiddenTotal} <Trans>Hidden</Trans>
                  </span>
                  {!!allFindingsAddedTotal && (
                    <span>
                      {allFindingsAddedTotal} <Trans>Added</Trans>
                    </span>
                  )}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <FontAwesomeIcon
                          icon={faCircleInfo}
                          className="vai-text-foreground-muted vai-h-3 vai-w-3"
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      portal={true}
                      variant="dark"
                      className="vai-max-w-[450px]"
                    >
                      <p>{findingsDescription || t`No details`}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              )}
            <button
              data-testid="expand-image-browser-button"
              type="button"
              aria-label={isExpanded ? t`Collapse` : t`Expand`}
              className="vai-h-9 vai-w-9 vai-flex vai-items-center vai-justify-center vai-rounded-md hover:vai-bg-zinc-700 vai-transition-colors vai-duration-200"
              onClick={handleExpandToggle}
            >
              <FontAwesomeIcon
                icon={
                  isExpanded
                    ? faDownLeftAndUpRightToCenter
                    : faUpRightAndDownLeftFromCenter
                }
                className="vai-text-white vai-h-5 vai-w-5"
              />
            </button>
          </div>
        </div>
        <ImageGrid
          visitSummary={visitSummary}
          onPaginationChange={handlePaginationChange}
          onPageChange={handlePageChange}
          currentPage={paginationData.currentPage}
        />
      </div>
    </>
  );
};
